import { z } from 'zod';
import { NorthStarKpiSchema } from '../events/workspace.js';

export const CurrencyCodeEnum = z.enum(['USD', 'EUR', 'GBP', 'ILS', 'AUD', 'CAD', 'JPY']);
export type CurrencyCode = z.infer<typeof CurrencyCodeEnum>;

// ─── Tenant Settings ────────────────────────────────────────────────
// API-facing DTOs for tenant-level configuration.
// FE reads TenantSettingsResponse, submits UpdateTenantSettingsBody.

export const TenantSettingsResponseSchema = z.object({
  id: z.string(),
  tenantId: z.string(),

  conversionsMonthly: z.number().int(),
  conversionsWeekly: z.number().int(),

  defaultIndustry: z.string().nullable(),
  defaultProductsComp: z.string().nullable(),
  defaultProductsCore: z.string().nullable(),
  defaultAccount: z.string().nullable(),

  currency: CurrencyCodeEnum,
  defaultPlatforms: z.string().nullable(),
  defaultBrand: z.string().nullable(),

  pageSettings: z.record(z.string(), z.unknown()).nullable(),

  defaultNorthStar: NorthStarKpiSchema.nullable(),

  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TenantSettingsResponse = z.infer<typeof TenantSettingsResponseSchema>;

// Self-serve `PUT /tenant-settings` payload. Intentionally narrow — this is
// the only shape the public route accepts; anything else is silently stripped.
// Broader, admin-only fields live in AdminUpdateTenantSettingsBodySchema below.
export const UpdateTenantSettingsBodySchema = z.object({
  defaultBrand: z.string().max(255).nullish(),
  defaultIndustry: z.string().max(255).nullish(),
  currency: CurrencyCodeEnum.optional(),
});
export type UpdateTenantSettingsBody = z.infer<typeof UpdateTenantSettingsBodySchema>;

/** Cap for tenant Marketplace orders inbox addresses. Matches vendor contactEmails. */
export const MARKETPLACE_ORDERS_INBOX_EMAIL_COUNT_MAX = 10;

const marketplaceOrdersInboxEmailsSchema = z
  .array(z.string().trim().email().max(255))
  .max(MARKETPLACE_ORDERS_INBOX_EMAIL_COUNT_MAX)
  .optional();

/** Expand-phase: older clients may still PUT the scalar. Blank or null clears. */
const marketplaceOrdersInboxEmailLegacySchema = z.preprocess(
  (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  },
  z.string().email().max(255).nullable().optional(),
);

// Admin-only `PUT /tenants/:tenantId/settings` payload. Superset of the
// self-serve fields plus tenant-provisioning knobs (conversions, north-star,
// customer partition) that only privileged admin flows may set.
export const AdminUpdateTenantSettingsBodySchema = z.object({
  defaultIndustry: z.string().max(255).nullable().optional(),
  defaultBrand: z.string().max(255).nullable().optional(),
  customerPartition: z.string().max(255).nullable().optional(),
  // Opts the tenant out of the cross-tenant BQ client guard (AD-2236). Admin-only
  // by construction: it is the sanctioned way to express deliberate sharing, so a
  // borrowed customerPartition stays distinguishable from a typo.
  isBqClientSharingApproved: z.boolean().optional(),
  conversionsMonthly: z.number().int().nonnegative().optional(),
  conversionsWeekly: z.number().int().nonnegative().optional(),
  currency: CurrencyCodeEnum.optional(),
  defaultNorthStar: NorthStarKpiSchema.nullable().optional(),
  // Empty array means "fall back to the ordering user". The route also writes
  // marketplaceOrdersInboxEmail as emails[0] ?? null (expand / write-both).
  marketplaceOrdersInboxEmails: marketplaceOrdersInboxEmailsSchema,
  marketplaceOrdersInboxEmail: marketplaceOrdersInboxEmailLegacySchema,
});
export type AdminUpdateTenantSettingsBody = z.infer<typeof AdminUpdateTenantSettingsBodySchema>;

/** Canonical array for admin PUT. Array wins; omitted fields leave inbox unchanged. */
export function resolveAdminInboxEmailsUpdate(body: AdminUpdateTenantSettingsBody): string[] | undefined {
  if (body.marketplaceOrdersInboxEmails !== undefined) return body.marketplaceOrdersInboxEmails;
  if (body.marketplaceOrdersInboxEmail !== undefined) {
    return body.marketplaceOrdersInboxEmail ? [body.marketplaceOrdersInboxEmail] : [];
  }
  return undefined;
}
